import { isEmpty, isNil } from 'lodash';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  Chip,
  Grid,
  IconButton,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
} from '@mui/material';
import { format } from 'date-fns';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { MANUFACTURER_PAGE_SIZE } from 'api/inventory';
import LoadingModule from 'common/components/LoadingModule';
import NothingFound from 'common/components/NothingFound';
import {
  listManufacturersByPage,
  updateManufacturerSort,
} from 'store/thunks/manufacturerManagementThunk';

const PRIMARY_DARK = '#021544';

const headCellSx = {
  color: '#fff',
  fontWeight: 700,
};

const SORT_ENUM = {
  manufacturer: { asc: 'MANUFACTURER_ASC', desc: 'MANUFACTURER_DESC' },
  createdAt: { asc: 'CREATED_AT_ASC', desc: 'CREATED_AT_DESC' },
};

const SORT_STATE = {
  MANUFACTURER_ASC: { orderBy: 'manufacturer', order: 'asc' },
  MANUFACTURER_DESC: { orderBy: 'manufacturer', order: 'desc' },
  CREATED_AT_ASC: { orderBy: 'createdAt', order: 'asc' },
  CREATED_AT_DESC: { orderBy: 'createdAt', order: 'desc' },
};

const DATA_COLUMNS = [
  { id: 'manufacturer', label: 'Manufacturer Name', sortable: true },
  { id: 'status', label: 'Status', sortable: false },
  { id: 'createdAt', label: 'Created At', sortable: true },
  { id: 'updatedAt', label: 'Last Updated', sortable: false },
];
const ACTIONS_COLUMN = { id: 'actions', label: 'Actions', sortable: false, align: 'right' };

const formatTimestamp = (value) => (value ? format(new Date(value), 'MM/dd/yyyy hh:mm a') : '—');

// onEdit / onRemove are only passed for roles allowed to use them; with neither
// (Inventory Viewer) the Actions column is left out.
const ManufacturerManagementTable = ({ onEdit, onRemove }) => {
  const dispatch = useDispatch();
  const hasActions = !!onEdit || !!onRemove;
  const columns = hasActions ? [...DATA_COLUMNS, ACTIONS_COLUMN] : DATA_COLUMNS;
  const { pages, currentPage, totalCount, sort } = useSelector(
    ({ inventory }) => inventory.manufacturerManagement
  );

  const manufacturers = pages[currentPage];
  const totalPages = Math.ceil(totalCount / MANUFACTURER_PAGE_SIZE);
  const { orderBy, order } = SORT_STATE[sort] ?? SORT_STATE.MANUFACTURER_ASC;

  const handleSort = (columnId) => {
    let nextOrder = 'asc';
    if (orderBy === columnId && order === 'asc') nextOrder = 'desc';
    dispatch(updateManufacturerSort({ sort: SORT_ENUM[columnId][nextOrder] }));
  };

  const handlePageChange = (_, page) => {
    if (currentPage === page) return;
    dispatch(listManufacturersByPage({ page }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Grid component={Paper} sx={{ width: '100%', overflow: 'hidden' }} container direction='column'>
      <Grid item>
        <TableContainer>
          <Table size='small'>
            <TableHead>
              <TableRow sx={{ backgroundColor: PRIMARY_DARK }}>
                {columns.map(({ id, label, sortable, align }) => (
                  <TableCell
                    key={id}
                    align={align}
                    sx={headCellSx}
                    sortDirection={orderBy === id ? order : false}
                  >
                    {sortable ? (
                      <TableSortLabel
                        active={orderBy === id}
                        direction={orderBy === id ? order : 'asc'}
                        onClick={() => handleSort(id)}
                        sx={{
                          color: '#fff !important',
                          '& .MuiTableSortLabel-icon': { color: '#fff !important' },
                        }}
                      >
                        {label}
                      </TableSortLabel>
                    ) : (
                      label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {!isNil(manufacturers) &&
                manufacturers.map((row) => (
                  <TableRow key={row.manufacturerId} hover>
                    <TableCell sx={{ color: row.isDeleted ? 'text.secondary' : undefined }}>
                      {row.manufacturer}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size='small'
                        label={row.isDeleted ? 'Inactive' : 'Active'}
                        color={row.isDeleted ? 'error' : 'success'}
                      />
                    </TableCell>
                    <TableCell>{formatTimestamp(row.createdAt)}</TableCell>
                    <TableCell>{formatTimestamp(row.updatedAt)}</TableCell>
                    {hasActions && (
                      <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>
                        {/* A removed manufacturer is history: it can't be edited or
                            removed again, only re-added as a new entry. */}
                        {!row.isDeleted && onEdit && (
                          <Tooltip title='Edit'>
                            <IconButton
                              size='small'
                              aria-label={`Edit ${row.manufacturer}`}
                              onClick={() => onEdit(row)}
                            >
                              <EditOutlinedIcon fontSize='small' />
                            </IconButton>
                          </Tooltip>
                        )}
                        {!row.isDeleted && onRemove && (
                          <Tooltip title='Remove'>
                            <IconButton
                              size='small'
                              color='error'
                              aria-label={`Remove ${row.manufacturer}`}
                              onClick={() => onRemove(row)}
                            >
                              <DeleteOutlineIcon fontSize='small' />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
            </TableBody>

            {!isNil(manufacturers) && totalPages > 1 && (
              <TableFooter>
                <TableRow sx={{ backgroundColor: PRIMARY_DARK }}>
                  <TableCell colSpan={columns.length} align='right' sx={{ borderBottom: 'none' }}>
                    <Pagination
                      sx={{
                        justifyContent: 'flex-end',
                        display: 'flex',
                        '& .MuiPaginationItem-root': { color: '#fff' },
                        // Default selected tint is invisible on the dark footer; invert it.
                        '& .MuiPaginationItem-root.Mui-selected, & .MuiPaginationItem-root.Mui-selected:hover':
                          {
                            backgroundColor: '#fff',
                            color: PRIMARY_DARK,
                            fontWeight: 700,
                          },
                      }}
                      count={totalPages}
                      page={currentPage}
                      size='small'
                      onChange={handlePageChange}
                    />
                  </TableCell>
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </TableContainer>
      </Grid>

      {isNil(manufacturers) && (
        <Grid item>
          <LoadingModule />
        </Grid>
      )}

      {!isNil(manufacturers) && isEmpty(manufacturers) && (
        <Grid item>
          <NothingFound />
        </Grid>
      )}
    </Grid>
  );
};

ManufacturerManagementTable.propTypes = {
  onEdit: PropTypes.func,
  onRemove: PropTypes.func,
};

ManufacturerManagementTable.defaultProps = {
  onEdit: undefined,
  onRemove: undefined,
};

export default ManufacturerManagementTable;
